import { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import { Target, Flag, Heart, Globe2, Sparkles, MapPin } from "lucide-react";
import api from "@/lib/api";

const MISSION = [
  { icon: MapPin, title: "Promote Destinations", text: "Take lesser-known destinations to national and international running communities." },
  { icon: Heart, title: "Promote Fitness", text: "Encourage active lifestyles and participation in running." },
  { icon: Globe2, title: "Support Communities", text: "Create opportunities for local businesses, volunteers, youth and service providers." },
  { icon: Sparkles, title: "Celebrate Culture", text: "Integrate local heritage, cuisine, traditions and stories into the participant experience." },
  { icon: Flag, title: "Create Experiences", text: "Transform an ordinary race into a memorable destination journey." },
];

export default function About() {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="ABOUT SPORTIVE"
        title="More Than a Run. It's an Experience."
        subtitle="SPORTIVE, also known as Real Sports India, is a sports tourism venture based in Kolkata, focused on conceptualising, organising and managing marathons, trail runs and destination-based sporting experiences."
        image="https://images.unsplash.com/photo-1524646349956-1590eacfa324?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtYXJhdGhvbiUyMHJ1bm5lcnMlMjBmaW5pc2glMjBsaW5lJTIwdmljdG9yeSUyMGF0aGxldGV8ZW58MHx8fHwxNzg5MDY4MTIzfDA&ixlib=rb-4.1.0&q=85"
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="eyebrow">WHO WE ARE</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">We Use Sport as a Platform</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">Our approach goes beyond organising a race. We use sport as a platform to build running and fitness culture, connect travellers with local communities, create economic opportunities around events, and showcase regional culture, heritage and natural landscapes — bringing runners from across India to new destinations.</p>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {["Promote emerging destinations", "Build running & fitness culture", "Connect travellers with communities", "Create local economic opportunities", "Showcase culture & heritage", "Bring runners to new destinations"].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gradient-to-r from-brand-cyan to-brand-olive" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <img src="https://images.unsplash.com/photo-1782145373397-19130f8a3114?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxydW5uZXIlMjBhdGhsZXRlJTIwcG9ydHJhaXQlMjBvdXRkb29yc3xlbnwwfHx8MTc4OTA2ODIwN3ww&ixlib=rb-4.1.0&q=85" alt="Runner on trail" className="rounded-3xl border border-brand-line object-cover" />
        </div>
      </section>

      <section className="border-y border-brand-line bg-brand-surface/40">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-20 sm:px-6 lg:grid-cols-2">
          <div className="card-k border-brand-cyan/30 p-8">
            <Target className="text-brand-cyan" size={28} />
            <p className="eyebrow mt-4">OUR VISION</p>
            <h3 className="mt-2 font-display text-3xl font-bold uppercase text-white">Making India Run.</h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">To create a nationwide ecosystem where running becomes a gateway to discovering India's diverse destinations, cultures, communities and landscapes. We envision a future where every race is more than a finish line — it is an opportunity to explore somewhere new.</p>
          </div>
          <div className="card-k border-brand-olive/30 p-8">
            <Flag className="text-brand-olive" size={28} />
            <p className="eyebrow mt-4 !text-brand-olive">OUR MISSION</p>
            <h3 className="mt-2 font-display text-3xl font-bold uppercase text-white">Sport + Tourism + Destinations</h3>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">To connect sport, tourism and destinations through professionally managed running experiences — for runners, for communities, and for the places we run through.</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <p className="eyebrow">WHAT DRIVES US</p>
        <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Five Goals. One Start Line.</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
          {MISSION.map((m) => (
            <div key={m.title} className="card-k p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-brand-cyan/50">
              <m.icon className="text-brand-cyan" size={22} />
              <h3 className="mt-4 font-display text-lg font-bold text-white">{m.title}</h3>
              <p className="mt-2 text-xs leading-relaxed text-slate-400">{m.text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="border-t border-brand-line bg-brand-surface/40">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="eyebrow">THE PEOPLE BEHIND SPORTIVE</p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white">Meet the Team</h2>
          <p className="mt-4 max-w-2xl text-slate-400">A compact crew of race directors, route scouts and community builders who turn India's most beautiful landscapes into start lines.</p>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((m) => (
              <div key={m.id} data-testid={`team-member-${m.id}`} className="card-k group overflow-hidden transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan/50">
                {m.photo && (
                  <div className="h-60 overflow-hidden">
                    <img src={m.photo} alt={m.name || m.role} className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                )}
                <div className="p-6">
                  {m.name ? (
                    <>
                      <h3 className="font-display text-2xl font-bold text-white">{m.name}</h3>
                      <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-brand-olive">{m.role?.toUpperCase()}</p>
                    </>
                  ) : (
                    <h3 className="font-display text-2xl font-bold text-brand-cyan">{m.role}</h3>
                  )}
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
